import { useI18n } from 'vue-i18n';
import type { PasswordPolicyContext } from '../helpers/passwordPolicy.helper';
import type { OutcomeVoucherRow } from '../interfaces';
import type { FieldSchema } from '../types';
import { isRichTextEmpty, toCents } from '@Helpers';
import { FORM_VALIDATIONS } from '../constants';
import {
  animalNameValidation,
  emailValidation,
  getPasswordValidationErrors,
  microchipValidation,
  phoneValidation,
  urlValidation,
} from '../helpers';

export default function () {
  const { t } = useI18n();
  // CASES
  function emailFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.validEmail';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = emailValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function urlFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.validUrl';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = urlValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function microchipFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.requiredMicrochipLength';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = microchipValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  // NOTE :: Outcome Vouchers: every picked voucher needs an expiration date or "No expiration".
  // A voucher type without "Valid for (days)" starts with no date, so staff must choose one.
  function outcomeVouchersFieldValidation(field: FieldSchema, wasFormSubmitted: boolean) {
    const ERROR_MESSAGE = 'components.forms.errors.voucherExpiration';
    const { state, model } = field;

    const rows: OutcomeVoucherRow[] = Array.isArray(model) ? model : [];
    const hasRowWithoutDate = rows.some((row) => !row.no_expiration && !row.exp_date);

    if (!hasRowWithoutDate) return state.errors.delete(ERROR_MESSAGE);
    if (!wasFormSubmitted) return;

    state.errors.add(ERROR_MESSAGE);
  }

  function amountFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.amount';
    const ERROR_MESSAGE_MISSING_UNIT = 'components.forms.errors.amountMissingUnit';

    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const { amount, amount_unit } = model;

    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);

    if (amount && typeof amount !== 'string') {
      model.amount = String(amount);
    }

    if ((amount && !amount_unit) || (amount_unit && !amount)) {
      state.errors.add(ERROR_MESSAGE_MISSING_UNIT);
      model.value = undefined;
      return;
    }

    if (!amount) {
      model.value = undefined;
      state.errors.delete(ERROR_MESSAGE);
      state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
      return;
    }

    if (amount_unit) {
      state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
    }

    if (typeof amount !== 'string' || amount !== amount.trim()) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
      return;
    }

    // NOTE: This portion of the code was done by Gerald.
    const doseRegex = /^((\d{0,4})?([.,]\d{1,4})?|\d{1,4} \d{1,4}\/\d{1,4}|\d{1,4}\/\d{1,4})$/;
    if (!doseRegex.test(amount)) {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
      return;
    }
    try {
      if (amount.includes('/')) {
        const parts = amount.split(' ');
        let whole = 0,
          numerator = 0,
          denominator = 1;
        if (parts.length === 2) {
          whole = parseInt(parts[0], 10);
          [numerator, denominator] = parts[1].split('/').map(Number);
        } else {
          [numerator, denominator] = parts[0].split('/').map(Number);
        }
        if (
          denominator === 0 ||
          whole.toString().length > 4 ||
          numerator.toString().length > 4 ||
          denominator.toString().length > 4
        ) {
          state.errors.add(ERROR_MESSAGE);
          model.value = undefined;
          return;
        }
        state.errors.delete(ERROR_MESSAGE);
        return;
      }
      if (amount.includes('.') || amount.includes(',')) {
        const normalized = amount.replace(',', '.');
        const [intPart, decimalPart] = normalized.split('.');
        if (intPart.length > 4 || decimalPart.length > 4) {
          state.errors.add(ERROR_MESSAGE);
          model.value = undefined;
          return;
        }
        state.errors.delete(ERROR_MESSAGE);
        return;
      }
      if (amount.length > 4) {
        state.errors.add(ERROR_MESSAGE);
        model.value = undefined;
        return;
      }
      state.errors.delete(ERROR_MESSAGE);
    } catch {
      state.errors.add(ERROR_MESSAGE);
      model.value = undefined;
    }

    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(ERROR_MESSAGE_MISSING_UNIT);
  }

  function phoneFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.validPhone';
    const { state, model, settings } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = phoneValidation(model, settings?.country);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function passwordFieldValidation(field: FieldSchema) {
    const { state, model, settings } = field;

    if (!settings?.passwordPolicy) return;

    const passwordContext = (settings?.passwordContext ?? {}) as PasswordPolicyContext;
    const policyErrors = getPasswordValidationErrors(String(model ?? ''), passwordContext);

    Object.values({
      length: 'components.formFields.password.errors.length',
      bannedWord: 'components.formFields.password.errors.bannedWord',
      context: 'components.formFields.password.errors.context',
      common: 'components.formFields.password.errors.common',
    }).forEach((errorKey) => state.errors.delete(errorKey));

    if (!model) return;

    policyErrors.forEach((errorKey) => state.errors.add(errorKey));
  }

  function animalNameFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'animals.nameField.invalidChars';
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = animalNameValidation(model);
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function emailTypeFieldValidation(field: FieldSchema) {
    const EMPTY_EMAIL = 'components.forms.errors.onePrimaryEmailRequired';
    const { state, model } = field;

    if (!model) return state.errors.delete(EMPTY_EMAIL);

    const hasPrimary = field.model.filter((entry: any) => entry.primary).length;
    if (hasPrimary) return state.errors.delete(EMPTY_EMAIL);

    state.errors.add(EMPTY_EMAIL);
  }

  function contactTypeFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE =
      field.name === 'emails' ? 'components.forms.errors.validEmailReview' : 'components.forms.errors.validPhoneReview';

    const { state, model } = field;

    const hasError = model?.some((entry: any) => entry.error);

    if (hasError) return state.errors.add(ERROR_MESSAGE);

    state.errors.delete(ERROR_MESSAGE);
  }

  function stripeFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.requiredStripe';
    const STRIPE_ERROR_KEY = 'components.forms.errors.stripeValidationError';
    const { state, model, settings } = field;

    if (!settings.required) {
      state.errors.delete(ERROR_MESSAGE);
      state.errors.delete(STRIPE_ERROR_KEY);
      return;
    }

    // NOTE :: A typo in the card number sets stripeError and cardComplete is false. Block submit.
    // A decline, or a closed bank check, also sets stripeError, but the card is still complete.
    // That must not block the next submit. Staff can change the name, or try the same card again.
    const hasCardElementError = !!model?.stripeError && !model?.cardComplete;
    if (hasCardElementError) {
      state.errors.add(STRIPE_ERROR_KEY);
      state.errors.delete(ERROR_MESSAGE);
      return;
    }

    // NOTE :: There is no token. Stripe reports `complete` as the card is typed. The charge is
    // confirmed in the browser on submit (`useStripeCardConfirm`).
    const emptyModelOnRequiredField = !model || !model.cardHolderName || !model.cardComplete;
    if (emptyModelOnRequiredField) {
      state.errors.add(ERROR_MESSAGE);
      state.errors.delete(STRIPE_ERROR_KEY);
      return;
    }

    // All validations passed
    state.errors.delete(ERROR_MESSAGE);
    state.errors.delete(STRIPE_ERROR_KEY);
  }

  function splitPaymentFieldValidation(field: FieldSchema, wasFormSubmitted: boolean) {
    const { state, model, settings } = field;

    if (!settings.required || !wasFormSubmitted) return state.errors.clear();

    // Minimum payment methods validation
    const amountFields = ['cash_amount', 'check_amount', 'other_amount', 'card_reader_amount', 'credit_card_amount'];
    const paymentMethods = amountFields.map((fieldName) => ({
      name: fieldName,
      amount: model[fieldName] || 0,
    }));

    const nonZeroPaymentMethods = paymentMethods.filter(
      ({ amount }) => amount !== null && amount !== undefined && Number(amount) > 0
    );

    if (nonZeroPaymentMethods.length < 2) {
      state.errors.clear();
      return state.errors.add('cart.splitPayment.minimumPaymentMethodsValidation');
    }

    const splitTotalCents = toCents(model.split_total);
    const cartTotalCents = toCents(model.cart_total);

    if (splitTotalCents !== cartTotalCents) {
      state.errors.clear();
      return state.errors.add('cart.splitPayment.totalAmountValidation');
    }

    const missinCreditCardData = Number(model.credit_card_amount) > 0 && (!model.name_on_card || !model.card_complete);
    if (missinCreditCardData) {
      state.errors.clear();
      return state.errors.add('components.forms.errors.requiredCreditCard');
    }

    // NOTE :: Same rule as the Stripe card field. Block a card number Stripe says is incomplete.
    // A decline leaves stripeError set on a card that is still complete. Do not block that retry.
    const hasCardElementError =
      Number(model.credit_card_amount) > 0 && !!model.stripe_card?.stripeError && !model.card_complete;
    if (hasCardElementError) {
      state.errors.clear();
      return state.errors.add('components.forms.errors.stripeValidationError');
    }

    state.errors.clear();
  }

  function moneyFieldValidation(field: FieldSchema) {
    const ERROR_MESSAGE = 'components.forms.errors.money';
    const { state, model } = field;

    if (model === 0) return state.errors.delete(ERROR_MESSAGE);

    const isRequiredWithouModel = (!model || isNaN(model)) && field.settings?.required;
    if (isRequiredWithouModel) return state.errors.add(ERROR_MESSAGE);

    state.errors.delete(ERROR_MESSAGE);
  }

  // CUSTOM VALIDATIONS
  function minLengthValidation(field: FieldSchema, rule: number) {
    const ERROR_MESSAGE = t('components.forms.errors.minLength', { length: rule });
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = model.length >= rule;
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  function maxLengthValidation(field: FieldSchema, rule: number) {
    const ERROR_MESSAGE = t('components.forms.errors.maxLength', { length: rule });
    const { state, model } = field;

    if (!model) return state.errors.delete(ERROR_MESSAGE);

    const isValid = model.length <= rule;
    if (isValid) return state.errors.delete(ERROR_MESSAGE);

    state.errors.add(ERROR_MESSAGE);
  }

  // VALIDATION TYPES
  function fieldTypeValidation(field: FieldSchema, wasFormSubmitted: boolean = false) {
    const { type } = field.settings;

    const needsValidation = [
      'amount_unit',
      'animal_name',
      'contact',
      'contact_type',
      'email',
      'email_type',
      'microchip',
      'money',
      'outcome_vouchers',
      'password',
      'split_payment',
      'stripe',
      'tel',
      'url',
    ];
    if (!type || !needsValidation.includes(type)) return;

    //prettier-ignore
    switch (type) {
      case 'amount_unit': return amountFieldValidation(field);
      case 'animal_name': return animalNameFieldValidation(field);
      case 'contact': return field.name === 'email' ? emailFieldValidation(field) : phoneFieldValidation(field);
      case 'contact_type': return contactTypeFieldValidation(field)
      case 'email': return emailFieldValidation(field);
      case 'email_type': return emailTypeFieldValidation(field);
      case 'money': return moneyFieldValidation(field);
      case 'microchip': return microchipFieldValidation(field);
      case 'outcome_vouchers': return outcomeVouchersFieldValidation(field, wasFormSubmitted);
      case 'password': return passwordFieldValidation(field);
      case 'split_payment': return splitPaymentFieldValidation(field, wasFormSubmitted);
      case 'stripe': return stripeFieldValidation(field);
      case 'tel': return phoneFieldValidation(field);
      case 'url': return urlFieldValidation(field);
    }
  }

  function fieldCustomValidation(field: FieldSchema) {
    if (!field.settings?.validations) return;

    const { validations } = field.settings;
    const { MAX_LENGTH, MIN_LENGTH } = FORM_VALIDATIONS;

    validations?.forEach(({ validation, rule }) => {
      //prettier-ignore
      switch (validation) {
        case MIN_LENGTH: return minLengthValidation(field, rule);
        case MAX_LENGTH: maxLengthValidation(field, rule);
      }
    });
  }

  function isResultModelMissingValue(model: any) {
    const isMissingArray = !Array.isArray(model) || !model.length;
    if (isMissingArray) return true;

    return model.some((entry: any) => {
      const resultValue = entry?.value;
      const isMissingValue = resultValue === null || resultValue === undefined || resultValue === '';

      return isMissingValue;
    });
  }

  function _requiredField(field: any) {
    const REQUIRED_ERROR_MESSAGE = 'components.forms.errors.required';
    const { model, settings, state } = field;
    const { required } = settings;

    // A rich-text field is a truthy HTML string even when it looks blank on screen. `AFieldEditor`
    // maps only Quill's exact `<p><br></p>` back to '', so `<p>&nbsp;</p>` or `<p>   </p>` — what a
    // stray keystroke or an applied template can leave behind — would satisfy a plain `!!model`.
    const isEmptyRichText = settings.type === 'editor' && isRichTextEmpty(model);
    const isEmptyResult = settings.type === 'result' && isResultModelMissingValue(model);

    // prettier-ignore
    const modelNotEmpty: boolean = isEmptyResult ? false
      : Array.isArray(model) ? !!model.length
      : model === 0 && settings.allowZeroAsValidModel ? true
      : isEmptyRichText ? false
      : !!model;

    if (required && !modelNotEmpty) {
      state.errors.add(REQUIRED_ERROR_MESSAGE);

      // NOTE: This is for debugging purposes for Gerald. Don't remove this.
      console.log('Errors', field.name, state.errors);
      return;
    }

    state.errors.delete(REQUIRED_ERROR_MESSAGE);
  }

  // HANDLER
  function validateField(field: FieldSchema, wasFormSubmitted: boolean = false) {
    if (field.settings.required && wasFormSubmitted) _requiredField(field);

    fieldTypeValidation(field, wasFormSubmitted);
    fieldCustomValidation(field);
  }

  return {
    emailFieldValidation,
    phoneFieldValidation,
    validateField,
  };
}
